import { atom } from 'jotai';
import { safeAtomFamily } from 'shared/utils/safe-atom-family';
import { Unreachable } from '@lemon8/web-app-shared/util';
import { IPrimaryComment } from 'shared/models/comment';
import { PromiseOrValue } from 'shared/utils/promise-or-value';

export interface IArticleComments {
  items: IPrimaryComment[];
  offset: number;
  hasMore: boolean;
}

type TArticleCommentsStore = {
  [key: string]: PromiseOrValue<IArticleComments> | undefined;
};

const $ArticleCommentsStore = atom({} as TArticleCommentsStore);
$ArticleCommentsStore.debugLabel = '$ArticleCommentsStore';

export const $ArticleComments = safeAtomFamily((groupId: string) => {
  const a = atom<PromiseOrValue<IArticleComments>, [PromiseOrValue<IArticleComments>], void>(
    (get) => {
      const data = get($ArticleCommentsStore)[groupId];
      if (!data) {
        return Unreachable();
      }

      if (data instanceof Promise) {
        return data.catch(() => ({
          items: [],
          offset: 0,
          hasMore: false,
        }));
      }

      return data;
    },
    (get, set, input: PromiseOrValue<IArticleComments>) => {
      const old = get($ArticleCommentsStore);
      set($ArticleCommentsStore, {
        ...old,
        [groupId]: input,
      });
    },
  );

  a.debugLabel = `$ArticleComments+${encodeURIComponent(groupId)}`;

  return a;
});

export const $ArticleCommentsLoadmore = safeAtomFamily((groupId: string) => {
  const a = atom(null, async (get, set, input: IArticleComments) => {
    const old = get($ArticleCommentsStore);
    const comment = await old[groupId];

    if (comment) {
      set($ArticleCommentsStore, {
        ...old,
        [groupId]: {
          ...input,
          items: [...comment.items, ...input.items],
        },
      });
    }
  });

  a.debugLabel = `$ArticleCommentsLoadmore+${encodeURIComponent(groupId)}`;

  return a;
});
